import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import ConfirmPasswordModal from '../../../components/feature/ConfirmPasswordModal';
import GererPersonnelModal from '../../../components/feature/GererPersonnelModal';
import BulkImportStaffModal from '../../../components/feature/BulkImportStaffModal';
import SpecialiteSelect from '../../../components/feature/SpecialiteSelect';

export default function InviteDoctorPage() {
  const { user, role, userRoles } = useAuth();
  const navigate = useNavigate();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const isTrueSuperAdmin = userRoles?.includes('super_admin') ?? false;
  const [loading, setLoading] = useState(true);
  const [invitations, setInvitations] = useState<any[]>([]);
  const [medecins, setMedecins] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'tokens' | 'medecins'>('tokens');

  const [showCreateInvitation, setShowCreateInvitation] = useState(false);
  const [invitationData, setInvitationData] = useState({
    nom: '',
    prenom: '',
    email: '',
    specialite: '',
  });
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [errorCreate, setErrorCreate] = useState('');
  const [successCreate, setSuccessCreate] = useState('');

  const [searchMedecin, setSearchMedecin] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  // Création directe de médecin
  const [showCreateDirect, setShowCreateDirect] = useState(false);
  const [directData, setDirectData] = useState({
    nom: '',
    prenom: '',
    email: '',
    specialite: '',
    telephone: '',
    numero_ordre: '',
    motDePasse: '',
    confirmPassword: '',
    sexe: '',
  });
  const [loadingDirect, setLoadingDirect] = useState(false);
  const [errorDirect, setErrorDirect] = useState('');
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  // Gérer un médecin
  const [selectedMedecin, setSelectedMedecin] = useState<any>(null);
  const [showGererModal, setShowGererModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const handleDirectSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorDirect('');

    // Si un mot de passe est fourni, valider la correspondance et la longueur
    if (directData.motDePasse) {
      if (directData.motDePasse !== directData.confirmPassword) {
        setErrorDirect('Les mots de passe ne correspondent pas');
        return;
      }
      if (directData.motDePasse.length < 10) {
        setErrorDirect('Le mot de passe doit contenir au moins 10 caractères');
        return;
      }
    }

    // Ouvrir la confirmation par mot de passe admin
    setShowPasswordConfirm(true);
  };

  const executeDirectCreation = async () => {
    setLoadingDirect(true);
    setErrorDirect('');

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) {
        setErrorDirect('Session expirée, veuillez vous reconnecter');
        setLoadingDirect(false);
        return;
      }

      const { data, error: fnError } = await supabase.functions.invoke('create-medecin-direct', {
        body: {
          nom: directData.nom,
          prenom: directData.prenom,
          email: directData.email,
          specialite: directData.specialite,
          telephone: directData.telephone || undefined,
          numero_ordre: directData.numero_ordre || undefined,
          motDePasse: directData.motDePasse,
          sexe: directData.sexe || null,
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (fnError || !data?.success) {
        throw new Error(data?.error || fnError?.message || 'Erreur lors de la création du médecin');
      }

      setSuccessCreate(`Dr ${directData.prenom} ${directData.nom} créé avec succès !`);
      setShowCreateDirect(false);
      setDirectData({ nom: '', prenom: '', email: '', specialite: '', telephone: '', numero_ordre: '', motDePasse: '', confirmPassword: '', sexe: '' });
      await loadMedecins();
      setTimeout(() => setSuccessCreate(''), 5000);
    } catch (err: any) {
      setErrorDirect(err.message || 'Erreur lors de la création du médecin');
    } finally {
      setLoadingDirect(false);
    }
  };

  useEffect(() => {
    checkSuperAdmin();
    loadInvitations();
    loadMedecins();
  }, [user]);

  const checkSuperAdmin = async () => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (role === 'super_admin' || role === 'admin') {
      setIsSuperAdmin(true);
      setIsAdminUser(true);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('super_admins')
      .select('role')
      .eq('user_id', user.id)
      .single();

    if (error || !data) {
      navigate('/', { replace: true });
      return;
    }

    setIsSuperAdmin(true);
    setIsAdminUser(true);
    setLoading(false);
  };

  const loadInvitations = async () => {
    const { data } = await supabase
      .from('invitations_medecins')
      .select('*')
      .order('created_at', { ascending: false });

    if (data) {
      setInvitations(data);
    }
  };

  const loadMedecins = async () => {
    const { data, error } = await supabase
      .from('medecins')
      .select(`
        id,
        user_id,
        nom,
        prenom,
        email,
        specialite,
        telephone,
        numero_ordre,
        photo_url,
        bloque,
        sexe,
        created_at,
        updated_at,
        rendez_vous (
          id,
          patient_id,
          statut,
          date_rdv,
          heure_debut,
          heure_fin,
          heure_debut_reel,
          heure_fin_reel
        )
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erreur chargement médecins:', error);
      return;
    }

    if (data) {
      const medecinsWithStats = data.map((medecin: any) => {
        const rdvs = medecin.rendez_vous || [];
        const rdvTermines = rdvs.filter((rdv: any) => rdv.statut === 'termine');
        
        let heuresTravail = 0;
        rdvTermines.forEach((rdv: any) => {
          if (rdv.heure_debut_reel && rdv.heure_fin_reel) {
            const today = new Date().toISOString().split('T')[0];
            const debut = new Date(`${today}T${rdv.heure_debut_reel}`);
            const fin = new Date(`${today}T${rdv.heure_fin_reel}`);
            heuresTravail += (fin.getTime() - debut.getTime()) / (1000 * 60 * 60);
          }
        });

        return {
          ...medecin,
          bloque: medecin.bloque || false,
          nombreConsultations: rdvTermines.length,
          nombrePatients: new Set(rdvs.map((rdv: any) => rdv.patient_id)).size,
          heuresTravail: Math.round(heuresTravail * 10) / 10,
          rdvEnAttente: rdvs.filter((rdv: any) => rdv.statut === 'en_attente').length,
          rdvEnCours: rdvs.filter((rdv: any) => rdv.statut === 'en_cours').length,
        };
      });

      setMedecins(medecinsWithStats);
    }
  };

  const createInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingCreate(true);
    setErrorCreate('');
    setSuccessCreate('');

    try {
      const { data, error: inviteError } = await supabase.functions.invoke('create-doctor-invitation', {
        body: {
          nom: invitationData.nom,
          prenom: invitationData.prenom,
          email: invitationData.email,
          specialite: invitationData.specialite,
        },
      });

      if (inviteError) throw inviteError;

      setSuccessCreate(`Token créé avec succès ! Token : ${data.token}`);
      setInvitationData({ nom: '', prenom: '', email: '', specialite: '' });
      setShowCreateInvitation(false);
      loadInvitations();
    } catch (err: any) {
      setErrorCreate(err.message || 'Erreur lors de la création du token');
    } finally {
      setLoadingCreate(false);
    }
  };

  const deleteInvitation = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce token ?')) return;

    try {
      const { error } = await supabase
        .from('invitations_medecins')
        .delete()
        .eq('id', id);

      if (error) throw error;
      loadInvitations();
      setSuccessCreate('Token supprimé avec succès');
      setTimeout(() => setSuccessCreate(''), 3000);
    } catch (err: any) {
      setErrorCreate('Erreur lors de la suppression');
      setTimeout(() => setErrorCreate(''), 3000);
    }
  };

  const toggleMedecinStatus = async (medecinId: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      const { error } = await supabase
        .from('medecins')
        .update({ bloque: newStatus })
        .eq('id', medecinId);

      if (error) throw error;
      
      setMedecins(prevMedecins => 
        prevMedecins.map(m => 
          m.id === medecinId ? { ...m, bloque: newStatus } : m
        )
      );
      
      setSuccessCreate(newStatus ? 'Médecin bloqué avec succès' : 'Médecin débloqué avec succès');
      setTimeout(() => setSuccessCreate(''), 3000);
    } catch (err: any) {
      setErrorCreate('Erreur lors de la modification du statut');
      setTimeout(() => setErrorCreate(''), 3000);
    }
  };

  const deleteMedecin = async (medecinId: string, userId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce médecin ? Cette action est irréversible.')) return;

    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;

      if (!token) {
        setErrorCreate('Session expirée, veuillez vous reconnecter');
        setTimeout(() => setErrorCreate(''), 3000);
        return;
      }

      const { data, error: fnError } = await supabase.functions.invoke('delete-medecin', {
        body: { medecinId, userId },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (fnError || !data?.success) {
        throw new Error(data?.error || fnError?.message || 'Erreur lors de la suppression');
      }

      setSuccessCreate('Médecin supprimé avec succès');
      await loadMedecins();
      setTimeout(() => setSuccessCreate(''), 3000);
    } catch (err: any) {
      setErrorCreate(err.message || 'Erreur lors de la suppression du médecin');
      setTimeout(() => setErrorCreate(''), 3000);
    }
  };

  const copyToken = (token: string) => {
    navigator.clipboard.writeText(token);
    setSuccessCreate('Token copié !');
    setTimeout(() => setSuccessCreate(''), 3000);
  };

  const filteredMedecins = medecins.filter(medecin => {
    const searchLower = searchMedecin.toLowerCase();
    return (
      medecin.nom.toLowerCase().includes(searchLower) ||
      medecin.prenom.toLowerCase().includes(searchLower) ||
      medecin.specialite.toLowerCase().includes(searchLower) ||
      medecin.email.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-clinic-blue-dark text-xl">Chargement...</div>
      </div>
    );
  }

  if (!isAdminUser) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-3xl shadow-xl border-2 border-clinic-blue/10 overflow-hidden">
          <div className="p-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-clinic-blue-dark">Gestion des Médecins</h1>
                <p className="text-gray-600 mt-2">Invitez et gérez les médecins de la clinique</p>
              </div>
              <button
                onClick={() => navigate('/admin')}
                className="px-4 py-2 text-clinic-blue hover:text-clinic-blue-dark transition-colors whitespace-nowrap"
              >
                ← Retour au tableau de bord
              </button>
            </div>

            {errorCreate && (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl flex items-start">
                <i className="ri-error-warning-line text-xl mr-2 mt-0.5"></i>
                <span>{errorCreate}</span>
              </div>
            )}

            {successCreate && (
              <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 text-green-600 rounded-2xl flex items-start">
                <i className="ri-check-line text-xl mr-2 mt-0.5"></i>
                <span>{successCreate}</span>
              </div>
            )}

            {/* Tabs */}
            <div className="mb-8">
              <div className="flex gap-2 border-b-2 border-gray-200">
                <button
                  onClick={() => setActiveTab('tokens')}
                  className={`px-6 py-3 font-semibold transition-all whitespace-nowrap ${
                    activeTab === 'tokens'
                      ? 'text-clinic-blue border-b-4 border-clinic-blue -mb-0.5'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className="ri-key-line mr-2"></i>
                  Tokens d'invitation ({invitations.length})
                </button>
                <button
                  onClick={() => setActiveTab('medecins')}
                  className={`px-6 py-3 font-semibold transition-all whitespace-nowrap ${
                    activeTab === 'medecins'
                      ? 'text-clinic-blue border-b-4 border-clinic-blue -mb-0.5'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className="ri-stethoscope-line mr-2"></i>
                  Médecins inscrits ({medecins.length})
                </button>
              </div>
            </div>

            {/* Contenu Tokens */}
            {activeTab === 'tokens' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-clinic-blue-dark">Tokens d'inscription</h2>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowImportModal(true)}
                      className="px-6 py-3 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-all font-semibold whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-file-upload-line mr-2"></i>Importer (CSV)
                    </button>
                    <button
                      onClick={() => setShowCreateDirect(true)}
                      className="px-6 py-3 bg-green-600 text-white rounded-full hover:bg-green-700 transition-all font-semibold shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-user-add-line mr-2"></i>
                      Créer un médecin directement
                    </button>
                    <button
                      onClick={() => setShowCreateInvitation(true)}
                      className="px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-add-line mr-2"></i>
                      Créer un token
                    </button>
                  </div>
                </div>

                {showCreateInvitation && (
                  <div className="mb-8 p-6 bg-clinic-blue/5 border-2 border-clinic-blue/20 rounded-2xl">
                    <h3 className="text-lg font-semibold text-clinic-blue-dark mb-4">Nouveau token médecin</h3>
                    <form onSubmit={createInvitation} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Nom</label>
                          <input
                            type="text"
                            required
                            value={invitationData.nom}
                            onChange={(e) => setInvitationData({ ...invitationData, nom: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Prénom</label>
                          <input
                            type="text"
                            required
                            value={invitationData.prenom}
                            onChange={(e) => setInvitationData({ ...invitationData, prenom: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Email</label>
                        <input
                          type="email"
                          required
                          value={invitationData.email}
                          onChange={(e) => setInvitationData({ ...invitationData, email: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Spécialité</label>
                        <SpecialiteSelect
                          value={invitationData.specialite}
                          onChange={(val) => setInvitationData({ ...invitationData, specialite: val })}
                          required
                        />
                      </div>
                      <div className="flex gap-4">
                        <button
                          type="submit"
                          disabled={loadingCreate}
                          className="px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg disabled:opacity-50 whitespace-nowrap cursor-pointer"
                        >
                          {loadingCreate ? 'Création...' : 'Créer le token'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowCreateInvitation(false)}
                          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-all font-semibold whitespace-nowrap cursor-pointer"
                        >
                          Annuler
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {showCreateDirect && (
                  <div className="mb-8 p-6 bg-green-50 border-2 border-green-200 rounded-2xl">
                    <h3 className="text-lg font-semibold text-green-800 mb-4">
                      <i className="ri-user-add-line mr-2"></i>
                      Créer un compte médecin directement
                    </h3>

                    {errorDirect && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm flex items-start">
                        <i className="ri-error-warning-line mr-2 mt-0.5 flex-shrink-0"></i>
                        <span>{errorDirect}</span>
                      </div>
                    )}

                    <form onSubmit={handleDirectSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Nom <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            required
                            value={directData.nom}
                            onChange={(e) => setDirectData({ ...directData, nom: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-green-200 rounded-2xl focus:outline-none focus:border-green-400 text-sm"
                            placeholder="Dupont"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Prénom <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            required
                            value={directData.prenom}
                            onChange={(e) => setDirectData({ ...directData, prenom: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-green-200 rounded-2xl focus:outline-none focus:border-green-400 text-sm"
                            placeholder="Jean"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sexe</label>
                        <select
                          value={directData.sexe}
                          onChange={(e) => setDirectData({ ...directData, sexe: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-green-200 rounded-2xl focus:outline-none focus:border-green-400 text-sm pr-8"
                        >
                          <option value="">Sélectionner</option>
                          <option value="Masculin">Masculin</option>
                          <option value="Féminin">Féminin</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
                        <input
                          type="email"
                          required
                          value={directData.email}
                          onChange={(e) => setDirectData({ ...directData, email: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-green-200 rounded-2xl focus:outline-none focus:border-green-400 text-sm"
                          placeholder="jean.dupont@email.com"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Spécialité <span className="text-red-500">*</span></label>
                          <SpecialiteSelect
                            value={directData.specialite}
                            onChange={(val) => setDirectData({ ...directData, specialite: val })}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                          <input
                            type="tel"
                            value={directData.telephone}
                            onChange={(e) => setDirectData({ ...directData, telephone: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-green-200 rounded-2xl focus:outline-none focus:border-green-400 text-sm"
                            placeholder="+33 6 12 34 56 78"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Numéro d'ordre</label>
                        <input
                          type="text"
                          value={directData.numero_ordre}
                          onChange={(e) => setDirectData({ ...directData, numero_ordre: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-green-200 rounded-2xl focus:outline-none focus:border-green-400 text-sm"
                          placeholder="12345678"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Mot de passe temporaire
                            <span className="text-xs text-gray-400 block mt-0.5">(optionnel si compte existant)</span>
                          </label>
                          <input
                            type="password"
                            value={directData.motDePasse}
                            onChange={(e) => setDirectData({ ...directData, motDePasse: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-green-200 rounded-2xl focus:outline-none focus:border-green-400 text-sm"
                            placeholder="Min. 10 caractères"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Confirmer le mot de passe
                            <span className="text-xs text-gray-400 block mt-0.5">(si nouveau compte)</span>
                          </label>
                          <input
                            type="password"
                            value={directData.confirmPassword}
                            onChange={(e) => setDirectData({ ...directData, confirmPassword: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-green-200 rounded-2xl focus:outline-none focus:border-green-400 text-sm"
                            placeholder="Répéter le mot de passe"
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700 flex items-start">
                        <i className="ri-information-line mr-2 mt-0.5 flex-shrink-0"></i>
                        <span>
                          <strong>Nouveau compte :</strong> un mot de passe temporaire est requis (min. 10 caractères). Communiquez-le au médecin.<br/>
                          <strong>Compte existant :</strong> laissez vide si l'utilisateur est déjà inscrit (ex: connexion Google). Le profil médecin sera lié à son compte actuel.
                        </span>
                      </div>
                      <div className="flex gap-4">
                        <button
                          type="submit"
                          disabled={loadingDirect}
                          className="px-6 py-3 bg-green-600 text-white rounded-full hover:bg-green-700 transition-all font-semibold shadow-lg disabled:opacity-50 whitespace-nowrap cursor-pointer"
                        >
                          {loadingDirect ? (
                            <>
                              <i className="ri-loader-4-line animate-spin mr-2"></i>
                              Création...
                            </>
                          ) : (
                            <>
                              <i className="ri-check-line mr-2"></i>
                              Créer le médecin
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowCreateDirect(false);
                            setErrorDirect('');
                          }}
                          disabled={loadingDirect}
                          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-all font-semibold whitespace-nowrap cursor-pointer disabled:opacity-50"
                        >
                          Annuler
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-clinic-blue/20">
                        <th className="text-left py-4 px-2 font-semibold text-clinic-blue-dark">Médecin</th>
                        <th className="text-left py-4 px-2 font-semibold text-clinic-blue-dark">Email</th>
                        <th className="text-left py-4 px-2 font-semibold text-clinic-blue-dark">Spécialité</th>
                        <th className="text-left py-4 px-2 font-semibold text-clinic-blue-dark">Token</th>
                        <th className="text-left py-4 px-2 font-semibold text-clinic-blue-dark">Statut</th>
                        <th className="text-center py-4 px-2 font-semibold text-clinic-blue-dark">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invitations.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-8 text-gray-500">
                            Aucun token créé pour le moment
                          </td>
                        </tr>
                      ) : (
                        invitations.map((invitation: any) => (
                          <tr key={invitation.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-4 px-2">
                              <div className="font-semibold text-clinic-blue-dark">
                                Dr {invitation.nom} {invitation.prenom}
                              </div>
                            </td>
                            <td className="py-4 px-2 text-gray-600">{invitation.email}</td>
                            <td className="py-4 px-2">
                              <span className="px-3 py-1 bg-clinic-blue/10 text-clinic-blue-dark rounded-full text-sm font-medium">
                                {invitation.specialite}
                              </span>
                            </td>
                            <td className="py-4 px-2">
                              <div className="flex items-center">
                                <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono mr-2">
                                  {invitation.token}
                                </code>
                                <button
                                  onClick={() => copyToken(invitation.token)}
                                  className="text-clinic-blue hover:text-clinic-blue-dark transition-colors"
                                  title="Copier le token"
                                >
                                  <i className="ri-file-copy-line"></i>
                                </button>
                              </div>
                            </td>
                            <td className="py-4 px-2">
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                invitation.used 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {invitation.used ? 'Utilisé' : 'En attente'}
                              </span>
                            </td>
                            <td className="py-4 px-2 text-center">
                              {!invitation.used && isTrueSuperAdmin && (
                              <button
                                onClick={() => deleteInvitation(invitation.id)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                                title="Supprimer"
                              >
                                <i className="ri-delete-bin-line text-lg"></i>
                              </button>
                            )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="mt-8 p-6 bg-blue-50 border-2 border-blue-200 rounded-2xl">
                  <h3 className="font-semibold text-blue-800 mb-2">Comment utiliser les tokens :</h3>
                  <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
                    <li>Créez un token avec les informations du médecin</li>
                    <li>Communiquez le token au médecin par email, téléphone ou autre moyen</li>
                    <li>Le médecin va sur la page d'inscription et choisit "Médecin"</li>
                    <li>Il saisit le token pour compléter son inscription</li>
                  </ol>
                </div>
              </div>
            )}

            {/* Contenu Médecins */}
            {activeTab === 'medecins' && (
              <div>
                <div className="mb-6">
                  <div className="relative">
                    <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
                    <input
                      type="text"
                      placeholder="Rechercher un médecin (nom, prénom, spécialité, email)..."
                      value={searchMedecin}
                      onChange={(e) => setSearchMedecin(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-6">
                  {filteredMedecins.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      {searchMedecin ? 'Aucun médecin trouvé' : 'Aucun médecin inscrit pour le moment'}
                    </div>
                  ) : (
                    filteredMedecins.map((medecin: any) => (
                      <div key={medecin.id} className="border-2 border-clinic-blue/10 rounded-2xl p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center">
                            <div className="w-16 h-16 bg-clinic-blue/10 rounded-full flex items-center justify-center mr-4">
                              <i className="ri-user-line text-3xl text-clinic-blue"></i>
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-clinic-blue-dark">
                                Dr {medecin.nom} {medecin.prenom}
                              </h3>
                              <p className="text-gray-600">{medecin.specialite}</p>
                              <p className="text-sm text-gray-500">{medecin.email}</p>
                              {medecin.bloque && (
                                <span className="inline-block mt-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                                  Compte bloqué
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => { setSelectedMedecin(medecin); setShowGererModal(true); }}
                              className="px-4 py-2 bg-clinic-blue/10 text-clinic-blue-dark rounded-full hover:bg-clinic-blue/20 font-medium transition-all whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-settings-3-line mr-1"></i>
                              Gérer
                            </button>
                            <button
                              onClick={() => toggleMedecinStatus(medecin.id, medecin.bloque)}
                              className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                                medecin.bloque
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                              }`}
                            >
                              <i className={`${medecin.bloque ? 'ri-lock-unlock-line' : 'ri-lock-line'} mr-1`}></i>
                              {medecin.bloque ? 'Débloquer' : 'Bloquer'}
                            </button>
                            {isTrueSuperAdmin && (
                              <button
                                onClick={() => deleteMedecin(medecin.id, medecin.user_id)}
                                className="px-4 py-2 bg-red-100 text-red-700 rounded-full hover:bg-red-200 font-medium transition-all whitespace-nowrap cursor-pointer"
                              >
                                <i className="ri-delete-bin-line mr-1"></i>
                                Supprimer
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                          <div className="bg-blue-50 p-4 rounded-xl">
                            <div className="text-2xl font-bold text-blue-700">{medecin.nombreConsultations}</div>
                            <div className="text-sm text-blue-600">Consultations</div>
                          </div>
                          <div className="bg-green-50 p-4 rounded-xl">
                            <div className="text-2xl font-bold text-green-700">{medecin.nombrePatients}</div>
                            <div className="text-sm text-green-600">Patients</div>
                          </div>
                          <div className="bg-purple-50 p-4 rounded-xl">
                            <div className="text-2xl font-bold text-purple-700">{medecin.heuresTravail}h</div>
                            <div className="text-sm text-purple-600">Heures travail</div>
                          </div>
                          <div className="bg-yellow-50 p-4 rounded-xl">
                            <div className="text-2xl font-bold text-yellow-700">{medecin.rdvEnAttente}</div>
                            <div className="text-sm text-yellow-600">En attente</div>
                          </div>
                          <div className="bg-orange-50 p-4 rounded-xl">
                            <div className="text-2xl font-bold text-orange-700">{medecin.rdvEnCours}</div>
                            <div className="text-sm text-orange-600">En cours</div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <BulkImportStaffModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        staffType="medecin"
        onSuccess={loadMedecins}
      />

      <ConfirmPasswordModal
        isOpen={showPasswordConfirm}
        title="Confirmation de création"
        message={`Vous allez créer le compte du Dr ${directData.prenom} ${directData.nom}. Cette action sera enregistrée dans les journaux d'accès. Veuillez confirmer avec votre mot de passe administrateur.`}
        confirmLabel="Créer le médecin"
        onConfirm={() => {
          setShowPasswordConfirm(false);
          executeDirectCreation();
        }}
        onCancel={() => setShowPasswordConfirm(false)}
      />

      <GererPersonnelModal
        isOpen={showGererModal}
        onClose={() => { setShowGererModal(false); setSelectedMedecin(null); }}
        type="medecin"
        personnel={selectedMedecin || { id: '', user_id: '', nom: '', prenom: '', email: '' }}
        onUpdate={loadMedecins}
      />

      <Footer />
    </div>
  );
}
